// ClientApp/src/hooks/useApi.js
// ---------------------------------------------------------------------------
// Tiny fetch helpers (same pattern as Gandalf). The Vite dev server proxies
// /api → ASP.NET dev URL (configured in vite.config.js), so relative URLs
// work in both dev and production. Controllers return camelCase JSON.
// ---------------------------------------------------------------------------

async function request(path, init = {}) {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: { Accept: "application/json", ...(init.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    let body = null;
    const text = await res.text().catch(() => "");
    try { body = text ? JSON.parse(text) : null; } catch { body = null; }
    const err = new Error(`HTTP ${res.status} ${res.statusText} — ${text}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  get:  (path)       => request(path),
  post: (path, body) => request(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: body == null ? undefined : JSON.stringify(body) }),
};

// ----- Filter → query-string ------------------------------------------------
// filter = { campuses, categories, sources, products, branches, from, to } (all optional)
export function filterToQuery(filter = {}) {
  const p = new URLSearchParams();
  for (const c of filter.campuses   ?? []) p.append("campus", c);
  for (const c of filter.categories ?? []) p.append("category", c);
  for (const s of filter.sources    ?? []) p.append("source", s);
  for (const pr of filter.products  ?? []) p.append("product", pr);
  for (const b of filter.branches   ?? []) p.append("branch", b);
  if (filter.from) p.set("from", filter.from);
  if (filter.to)   p.set("to", filter.to);
  return p.toString();
}

// ----- Per-page convenience wrappers ----------------------------------------

const withRun = (qs, runId) => (runId ? `${qs}${qs ? "&" : ""}runId=${runId}` : qs);

export const AnalyticsApi = {
  health:    ()                        => api.get("/api/health"),
  meta:      (runId)                   => api.get(`/api/meta?${withRun("", runId)}`),
  summary:   (filter, runId)           => api.get(`/api/summary?${withRun(filterToQuery(filter), runId)}`),
  breakdown: (groupBy, filter, runId)  => api.get(`/api/breakdown?groupBy=${encodeURIComponent(groupBy)}&${withRun(filterToQuery(filter), runId)}`),
  transactions: (filter, runId, limit, offset) => api.get(`/api/transactions?${withRun(`${filterToQuery(filter)}${limit ? `&limit=${limit}` : ""}${offset ? `&offset=${offset}` : ""}`, runId)}`),
};

// Cross-stream value-added analytics (the Insights page) + the readiness matrix.
export const InsightsApi = {
  readiness:   ()             => api.get("/api/wf/readiness"),
  overview:    (filter, runId) => api.get(`/api/wf/overview?${withRun(filterToQuery(filter), runId)}`),
  trends:      (runId)   => api.get(`/api/wf/trends?${withRun("", runId)}`),
  operations:  (runId)   => api.get(`/api/wf/operations?${withRun("", runId)}`),
  customers:   (runId)   => api.get(`/api/wf/customers?${withRun("", runId)}`),
  cashrisk:    (runId)   => api.get(`/api/wf/cashrisk?${withRun("", runId)}`),
  // Gross-margin measure: per-dimension margin breakdown + total (recipe standard-cost model).
  marginBreakdown: (groupBy, filter, runId) => api.get(`/api/wf/margin-breakdown?groupBy=${encodeURIComponent(groupBy)}&${withRun(filterToQuery(filter), runId)}`),
  marginSummary:   (filter, runId)          => api.get(`/api/wf/margin-summary?${withRun(filterToQuery(filter), runId)}`),
  // Gross margin on raw materials (Report section): { total, categories, materials, trend }.
  rawMaterialMargin: (filter, runId)         => api.get(`/api/wf/raw-material-margin?${withRun(filterToQuery(filter), runId)}`),
  // Gross margin by class — Recipe/blends vs Raw materials vs Other (Report section): { total, classes }.
  marginByClass: (filter, runId)             => api.get(`/api/wf/margin-by-class?${withRun(filterToQuery(filter), runId)}`),
  // AI executive summary — a cached READ only (pre-generated by the server when data is published,
  // so viewing never costs tokens). Returns { status, summary, reasoning?, comments?, model, generatedAt }.
  // There is deliberately no manual (re)generate — generation is automatic.
  aiSummary: (runId) => api.get(`/api/wf/ai-summary?${withRun("", runId)}`),
  // Save the human comment shown to tenant users (admin/client only; per data snapshot).
  aiSummaryComments: (runId, comments) => api.post(`/api/wf/ai-summary/comments?${withRun("", runId)}`, { comments }),
  // Override the AI summary text (admin/client only; per data snapshot). Send { bullets } to edit the
  // Summary view or { summary } for the Detailed view; the other keeps its existing override/AI text.
  aiSummaryEdit: (runId, body) => api.post(`/api/wf/ai-summary/edit?${withRun("", runId)}`, body),
  aiSummaryEditReset: (runId) => api.post(`/api/wf/ai-summary/edit/reset?${withRun("", runId)}`),
};

export const AuthApi = {
  me:        () => api.get("/api/auth/me"),
  config:    () => api.get("/api/auth/config"),
  devSignin: (email) => api.post(`/api/auth/dev-signin?email=${encodeURIComponent(email)}`),
  // Email + password sign-in for users without a Google account (admin-provisioned password).
  passwordSignin: (email, password) => api.post("/api/auth/password-signin", { email, password }),
  logout:    () => api.post("/api/auth/logout"),
};

export const UsersApi = {
  list:   ()          => api.get("/api/admin/users"),
  create: (user)      => api.post("/api/admin/users", user),
  update: (id, user)  => request(`/api/admin/users/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(user) }),
  remove: (id)        => request(`/api/admin/users/${id}`, { method: "DELETE" }),
  // Set/reset a user's email+password login (empty password clears it → Google-only).
  setPassword: (id, password) => api.post(`/api/admin/users/${id}/password`, { password }),
};

export const RunsApi = {
  list:    () => api.get("/api/runs"),
  current: () => api.get("/api/runs/current"),
  // Runs safe to pin in the Compare "Snapshot" picker: clean, same (invoice) stream, artifacts present.
  comparable: () => api.get("/api/runs/comparable"),
};

export const PipelineApi = {
  sources:       ()                      => api.get("/api/admin/sources"),
  ingest:        (source)                => api.post(`/api/admin/ingest?source=${encodeURIComponent(source)}`),
  ingestRuns:    ()                      => api.get("/api/admin/ingest/runs"),
  preview:       (runId, table, limit)   => api.get(`/api/admin/runs/${runId}/preview?table=${table}&limit=${limit ?? 25}`),
  columns:       (runId, table)          => api.get(`/api/admin/runs/${runId}/columns?table=${table}`),
  qualityReport: (runId)                 => api.get(`/api/admin/runs/${runId}/quality-report`),
};
